import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FC, ReactNode, createContext, useContext, useState } from 'react';

import { setModal } from 'modules/_shared/redux/modalActions';
import { useGetShareTitles } from 'modules/partners/hooks/useGetShareTitles';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { useGetPartnersSectionPermissions } from 'modules/partners/hooks/useGetPartnerSectionsPermissions';
import { ChangeSharesTitleStatusModalProvider } from 'modules/partners/modals/ChangeTitleStatusModal/context/ChangeSharesTitleStatusModalContext';
import ChangeSharesTitleStatusModal from 'modules/partners/modals/ChangeTitleStatusModal';

import { ShareTitleListPageContextType } from '../types/ShareTitleListPageContextType';
import { shareTitleListPageInitValue } from '../constants/shareTitleListPageInitValue';
import { useGetSocietyShareTitleDocument } from '../hooks/useGetSocietyShareTitleDocument';

const ShareTitleListPageContext = createContext<ShareTitleListPageContextType>(
  shareTitleListPageInitValue
);

export const ShareTitleListPageProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { societyId } = useParams();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const sharesTitles = useSelector((state: State) => state.sharesTitles || []);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const { data: societyShareTitleDocumentData, isLoading: isLoadingDocument } =
    useGetSocietyShareTitleDocument({ society: actualSociety });

  useGetActualSociety(societyId);
  useGetMenu();

  const { data: permissionsData } = useGetPartnersSectionPermissions();

  const { isLoading: isLoadingTitles } = useGetShareTitles({
    societyId,
    page,
    rowsPerPage,
    searchTerm,
  });

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleEdit = (index: number) => {
    const title = sharesTitles?.[index];
    if (!title) return;

    store.dispatch(
      setModal(
        <ChangeSharesTitleStatusModalProvider sharesTitle={title}>
          <ChangeSharesTitleStatusModal />
        </ChangeSharesTitleStatusModalProvider>
      )
    );
  };

  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
    setPage(1);
  };

  return (
    <ShareTitleListPageContext.Provider
      value={{
        data: {
          shareTitleTotal: actualSociety?.sharesTitlesCount?.total || 0,
          activeShareTitleTotal: actualSociety?.sharesTitlesCount?.active || 0,
          shareTitles: sharesTitles,
          societyShareTitleDocument:
            societyShareTitleDocumentData.document || null,
          societyId: actualSociety?._id || '',
          societyName: actualSociety?.name || '',
          corporateId: actualSociety?.corporateId || '',
          backgroundImage:
            actualSociety?.configuration?.sharesTitles?.backgroundImage || '',
          textPadding:
            actualSociety?.configuration?.sharesTitles?.textPadding || {},
          isLanscape:
            actualSociety?.configuration?.sharesTitles?.isLandscape || false,
          page,
          rowsPerPage,
          showPagination: searchTerm === '',
          isAdmin: permissionsData?.isAdmin || false,
          isReadOnly: permissionsData?.isReadOnly || false,
          isLoading: isLoadingTitles || isLoadingDocument,
        },
        actions: {
          handleChangePage,
          handleChangeRowsPerPage,
          handleSearch,
          handleEdit,
        },
      }}
    >
      {children}
    </ShareTitleListPageContext.Provider>
  );
};

export const useShareTitleListPage = () => {
  const context = useContext(ShareTitleListPageContext);
  if (!context) {
    throw new Error(
      'useShareTitleListPage must be used within a ShareTitleListPageProvider'
    );
  }
  return context;
};
